const LEGACY_VOTER_ID_STORAGE_KEY = 'voter_id'
const SCOPED_VOTER_ID_STORAGE_PREFIX = 'voter_id__scoped__'
const ACTIVE_VOTER_SCOPE_STORAGE_KEY = 'voter_id__active_scope'

interface VoterStorageScope {
  ballotId?: string | null
  customerId?: string | null
}

const canUseLocalStorage = (): boolean => {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
}

const getScopeValue = ({
  ballotId,
  customerId,
}: VoterStorageScope): string | null => {
  if (ballotId) return `ballot:${ballotId}`
  if (customerId) return `customer:${customerId}`
  return null
}

const removeNonCurrentScopedVoterIds = (currentStorageKey: string): void => {
  const keysToDelete: string[] = []
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index)
    if (!key || !key.startsWith(SCOPED_VOTER_ID_STORAGE_PREFIX)) continue
    if (key !== currentStorageKey) keysToDelete.push(key)
  }

  keysToDelete.forEach(key => localStorage.removeItem(key))
}

export const getActiveVoterScope = (): string | null => {
  if (!canUseLocalStorage()) return null
  return localStorage.getItem(ACTIVE_VOTER_SCOPE_STORAGE_KEY)
}

export const hasActiveVoterScope = (scope: VoterStorageScope): boolean => {
  const scopeValue = getScopeValue(scope)
  if (!scopeValue) return false
  return getActiveVoterScope() === scopeValue
}

export const isVoterStorageEventKey = (key: string | null): boolean => {
  if (!key) return false
  return (
    key === LEGACY_VOTER_ID_STORAGE_KEY
    || key === ACTIVE_VOTER_SCOPE_STORAGE_KEY
    || key.startsWith(SCOPED_VOTER_ID_STORAGE_PREFIX)
  )
}

export const getScopedVoterStorageKey = (scope: VoterStorageScope): string | null => {
  const scopeValue = getScopeValue(scope)
  if (!scopeValue) return null
  return `${SCOPED_VOTER_ID_STORAGE_PREFIX}${scopeValue}`
}

export const getStoredVoterId = (scope: VoterStorageScope): string | null => {
  if (!canUseLocalStorage()) return null
  const storageKey = getScopedVoterStorageKey(scope)
  if (!storageKey) return null
  return localStorage.getItem(storageKey)
}

export const setStoredVoterId = (voterId: string, scope: VoterStorageScope): void => {
  if (!canUseLocalStorage() || !voterId) return
  const scopeValue = getScopeValue(scope)
  if (!scopeValue) return

  const storageKey = getScopedVoterStorageKey(scope)
  if (!storageKey) return

  removeNonCurrentScopedVoterIds(storageKey)
  localStorage.setItem(ACTIVE_VOTER_SCOPE_STORAGE_KEY, scopeValue)
  localStorage.setItem(storageKey, voterId)
}

export const removeStoredVoterId = (scope: VoterStorageScope): void => {
  if (!canUseLocalStorage()) return
  localStorage.removeItem(LEGACY_VOTER_ID_STORAGE_KEY)

  const scopeValue = getScopeValue(scope)
  if (scopeValue && localStorage.getItem(ACTIVE_VOTER_SCOPE_STORAGE_KEY) === scopeValue) {
    localStorage.removeItem(ACTIVE_VOTER_SCOPE_STORAGE_KEY)
  }

  const storageKey = getScopedVoterStorageKey(scope)
  if (!storageKey) return
  localStorage.removeItem(storageKey)
}

export const clearAllStoredVoterIds = (): void => {
  if (!canUseLocalStorage()) return

  const keysToDelete: string[] = []
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index)
    if (key && key.startsWith(SCOPED_VOTER_ID_STORAGE_PREFIX)) {
      keysToDelete.push(key)
    }
  }

  localStorage.removeItem(LEGACY_VOTER_ID_STORAGE_KEY)
  localStorage.removeItem(ACTIVE_VOTER_SCOPE_STORAGE_KEY)
  keysToDelete.forEach(key => localStorage.removeItem(key))
}
